import {
  Box,
  Flex,
  Grid,
  ModalBody,
  ModalFooter,
  Text,
} from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";

interface OrderSubmitUnderMinimumProps {
  // 현재 주문금액(합계 행과 같은 값). 금액을 알 수 없으면 undefined.
  currentAmount: number | undefined;
  // 최소주문금액(제출 하한)
  minimumAmount: number;
  // 최소주문금액까지 모자란 금액. 주문금액을 알 수 없으면 undefined.
  shortfall: number | undefined;
  currency: string;
  // 모달을 닫고 주문서로 돌아간다
  onEdit: () => void;
  // 패스픽 상품 찾기로 이동
  onPathpick: () => void;
  // 떠나기 전에 주문서를 저장하는 중 — 버튼에 로딩을 띄우고 다른 버튼을 막는다
  isPathpickLoading?: boolean;
  // '협의된 추가 오더'로 제출하기 — 없으면 이 안내를 보여주지 않는다(담은 금액이 없을 때 등).
  onAdditionalOrder?: () => void;
}

/**
 * 오더 제출 모달의 '최소주문금액 미달' 화면(본문 + 버튼).
 * 주문금액이 최소주문금액(제출 하한)보다 작으면 제출 화면 대신 이걸 띄운다 —
 * 얼마가 모자란지 보여주고, 소량 주문은 패스픽(단품 주문)으로 안내한다.
 */
export default function OrderSubmitUnderMinimum({
  currentAmount,
  minimumAmount,
  shortfall,
  currency,
  onEdit,
  onPathpick,
  isPathpickLoading = false,
  onAdditionalOrder,
}: OrderSubmitUnderMinimumProps) {
  const { t, tCurrencyString } = useI18nHelper();
  const format = (amount: number | undefined) =>
    typeof amount === "number" && Number.isFinite(amount)
      ? tCurrencyString(amount, { type: "SYMBOL", code: currency })
      : "-";

  return (
    <>
      <ModalBody padding={"20px 24px"} color={"#444440"}>
        <Flex flexDirection={"column"} gap={"20px"}>
          <Box
            role={"alert"}
            background={"#FFEEEE"}
            color={"#CC1D1D"}
            padding={"12px 16px"}
            borderRadius={"4px"}
          >
            <Text
              as={"h2"}
              fontSize={"16px"}
              fontWeight={600}
              lineHeight={"24px"}
            >
              {t("moq_notice.under_min.headline")}
            </Text>
            <Text marginTop={"4px"} fontSize={"13px"} lineHeight={"20px"}>
              {t("moq_notice.under_min.description")}
            </Text>
          </Box>
          <Box>
            <Text fontSize={"14px"} fontWeight={600} marginBottom={"16px"}>
              {t("moq_notice.under_min.conditions")}
            </Text>
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(3, minmax(0, 1fr))" }}
              gap={{ base: "12px", md: 0 }}
              alignItems={"center"}
            >
              <Box textAlign={"center"}>
                <Text fontSize={"12px"} marginBottom={"4px"}>
                  {t("moq_notice.under_min.current_amount")}
                </Text>
                <Text fontSize={"16px"} fontWeight={700}>
                  {format(currentAmount)}
                </Text>
              </Box>
              <Box
                textAlign={"center"}
                borderLeft={{ base: 0, md: "1px solid #D4D4D1" }}
                borderRight={{ base: 0, md: "1px solid #D4D4D1" }}
                paddingX={"8px"}
              >
                <Text fontSize={"12px"} marginBottom={"4px"}>
                  {t("moq_notice.under_min.minimum_amount")}
                </Text>
                <Text fontSize={"16px"} fontWeight={700}>
                  {format(minimumAmount)}
                </Text>
              </Box>
              <Text
                textAlign={"center"}
                fontSize={"16px"}
                fontWeight={700}
                color={"#CC1D1D"}
                paddingX={"8px"}
              >
                {shortfall === undefined
                  ? t("moq_notice.under_min.amount_check_needed")
                  : t("moq_notice.under_min.shortfall", {
                      amount: format(shortfall),
                    })}
              </Text>
            </Grid>
          </Box>
          <Box borderTop={"1px solid #D4D4D1"} paddingTop={"20px"}>
            <Text
              as={"h3"}
              color={"#1272EF"}
              fontSize={"16px"}
              fontWeight={600}
              lineHeight={"24px"}
            >
              {t("moq_notice.under_min.pathpick_title")}
            </Text>
            <Text marginTop={"6px"} fontSize={"13px"} lineHeight={"20px"}>
              {t("moq_notice.under_min.pathpick_description")}
            </Text>
          </Box>
        </Flex>
      </ModalBody>
      <ModalFooter
        padding={"0 24px 24px"}
        flexDirection={"column"}
        alignItems={"stretch"}
      >
        <Flex
          gap={"12px"}
          flexDirection={{ base: "column", md: "row" }}
          alignItems={"stretch"}
        >
          <AppButton
            height={"40px"}
            isDisabled={isPathpickLoading}
            onClick={onEdit}
            chakraProps={{
              flex: { base: "0 0 auto", md: 1 },
              minH: "40px",
              px: "12px",
              fontSize: "13px",
            }}
          >
            {t("moq_notice.under_min.edit_order")}
          </AppButton>
          <AppButton
            height={"40px"}
            variant={"primary"}
            isLoading={isPathpickLoading}
            onClick={onPathpick}
            chakraProps={{
              flex: { base: "0 0 auto", md: 1.25 },
              minH: "40px",
              px: "12px",
              fontSize: "13px",
            }}
          >
            {t("moq_notice.under_min.find_on_pathpick")}
          </AppButton>
        </Flex>
        {onAdditionalOrder && (
          <Box
            borderTop={"1px solid #D4D4D1"}
            marginTop={"16px"}
            paddingTop={"12px"}
            color={"#6F6F6C"}
          >
            <Text fontSize={"12px"} fontWeight={500} lineHeight={"16px"}>
              {t("moq_notice.under_min.additional_order_question")}
            </Text>
            <Text
              marginTop={"4px"}
              fontSize={"12px"}
              fontWeight={400}
              lineHeight={"16px"}
            >
              {t("moq_notice.under_min.additional_order_description")}
            </Text>
            <AppButton
              variant={"ghost"}
              height={"28px"}
              isDisabled={isPathpickLoading}
              onClick={onAdditionalOrder}
              chakraProps={{
                mt: "4px",
                p: 0,
                minW: 0,
                fontSize: "12px",
                fontWeight: 400,
                lineHeight: "16px",
                color: "#6F6F6C",
                _hover: { color: "#1272EF", textDecoration: "underline" },
              }}
            >
              {t("moq_notice.under_min.additional_order_submit")}
              <span aria-hidden>{" →"}</span>
            </AppButton>
          </Box>
        )}
      </ModalFooter>
    </>
  );
}
